import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MapPin, Upload, Phone, Mail, User, Home, FileText, CheckCircle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { subscriptionPlans } from '../data/mock';
import { useToast } from '../hooks/use-toast';

const Apply = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const preSelectedPlan = location.state?.selectedPlan || '';

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    barangay: '',
    municipality: '',
    locationLink: '',
    selectedPlan: preSelectedPlan,
    idDocument: null,
    additionalNotes: ''
  });

  const [idFileName, setIdFileName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allPlans = [...subscriptionPlans.premium, ...subscriptionPlans.economy];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please upload an image smaller than 5MB",
          variant: "destructive"
        });
        return;
      }
      setFormData(prev => ({ ...prev, idDocument: file }));
      setIdFileName(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('barangay', formData.barangay);
      formDataToSend.append('municipality', formData.municipality);
      formDataToSend.append('locationLink', formData.locationLink || '');
      formDataToSend.append('selectedPlan', formData.selectedPlan);
      formDataToSend.append('additionalNotes', formData.additionalNotes || '');
      
      if (formData.idDocument) {
        formDataToSend.append('idDocument', formData.idDocument);
      }

      const response = await fetch(`${API_URL}/api/forms/ftth-application`, {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Application Submitted Successfully! ✅",
          description: data.message || "Check your email for next steps. Our team will contact you within 48 hours.",
        });
        
        // Redirect to home after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        throw new Error(data.detail || 'Submission failed');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: "Submission Error",
        description: "There was a problem submitting your application. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearIdDocument = () => {
    setFormData(prev => ({ ...prev, idDocument: null }));
    setIdFileName('');
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Apply for <span className="text-cyan-400">FTTH Service</span>
          </h1>
          <p className="text-xl text-gray-300">
            Fill out the form below and we'll schedule a site survey within 24-48 hours
          </p>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-700 rounded-2xl p-8">
            {/* Personal Information */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <User className="text-cyan-400 mr-2" size={24} />
                Personal Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="fullName" className="text-white mb-2 block">
                    Full Name *
                  </Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={handleChange}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="Juan Dela Cruz"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-white mb-2 block">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="+63 912 345 6789"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="email" className="text-white mb-2 block">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="juan@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <MapPin className="text-cyan-400 mr-2" size={24} />
                Installation Location
              </h2>
              
              <div className="space-y-6">
                <div>
                  <Label htmlFor="address" className="text-white mb-2 block">
                    Street Address *
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    required
                    value={formData.address}
                    onChange={handleChange}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="123 Main Street, Purok 5"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="barangay" className="text-white mb-2 block">
                      Barangay *
                    </Label>
                    <Input
                      id="barangay"
                      name="barangay"
                      type="text"
                      required
                      value={formData.barangay}
                      onChange={handleChange}
                      className="bg-slate-800 border-slate-600 text-white"
                      placeholder="San Juan"
                    />
                  </div>

                  <div>
                    <Label htmlFor="municipality" className="text-white mb-2 block">
                      Municipality *
                    </Label>
                    <Input
                      id="municipality"
                      name="municipality"
                      type="text"
                      required
                      value={formData.municipality}
                      onChange={handleChange}
                      className="bg-slate-800 border-slate-600 text-white"
                      placeholder="Sogod, Southern Leyte"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="locationLink" className="text-white mb-2 block">
                    Google Maps Link (Optional but Recommended)
                  </Label>
                  <Input
                    id="locationLink"
                    name="locationLink"
                    type="url"
                    value={formData.locationLink}
                    onChange={handleChange}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="https://maps.google.com/?q=..."
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    💡 Tip: Open Google Maps, drop a pin at your location, and share the link here for faster site survey
                  </p>
                </div>
              </div>
            </div>

            {/* Plan Selection */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <Home className="text-cyan-400 mr-2" size={24} />
                Plan Selection
              </h2>
              
              <div>
                <Label htmlFor="selectedPlan" className="text-white mb-2 block">
                  Choose Your Plan *
                </Label>
                <Select 
                  value={formData.selectedPlan} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, selectedPlan: value }))}
                  required
                >
                  <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                    <SelectValue placeholder="Select a plan" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600">
                    <div className="px-2 py-1.5 text-xs font-semibold text-cyan-400">Premium Plans</div>
                    {subscriptionPlans.premium.map(plan => (
                      <SelectItem key={plan.id} value={plan.name} className="text-white">
                        {plan.name} - {plan.price}/mo ({plan.speed})
                      </SelectItem>
                    ))}
                    <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 mt-2">Economy Plans</div>
                    {subscriptionPlans.economy.map(plan => (
                      <SelectItem key={plan.id} value={plan.name} className="text-white">
                        {plan.name} - {plan.price}/mo ({plan.speed})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ID Upload */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <FileText className="text-cyan-400 mr-2" size={24} />
                KYC Document
              </h2>
              
              <div>
                <Label htmlFor="idDocument" className="text-white mb-2 block">
                  Upload Valid ID *
                </Label>
                <p className="text-sm text-gray-400 mb-3">
                  Accepted: National ID, Driver's License, Passport, Voter's ID (Max 5MB, JPG/PNG)
                </p>
                
                {!idFileName ? (
                  <label 
                    htmlFor="idDocument"
                    className="flex items-center justify-center w-full p-6 border-2 border-dashed border-slate-600 rounded-xl hover:border-cyan-500 cursor-pointer transition-colors bg-slate-800/30"
                  >
                    <div className="text-center">
                      <Upload className="mx-auto text-cyan-400 mb-2" size={32} />
                      <span className="text-white">Click to upload ID document</span>
                      <p className="text-xs text-gray-400 mt-1">JPG, PNG • Max 5MB</p>
                    </div>
                    <input
                      id="idDocument"
                      name="idDocument"
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      onChange={handleFileUpload}
                      className="hidden"
                      required
                    />
                  </label>
                ) : (
                  <div className="flex items-center justify-between p-4 bg-slate-800 border border-slate-600 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="text-green-400" size={24} />
                      <div>
                        <p className="text-white font-medium">{idFileName}</p>
                        <p className="text-xs text-gray-400">Document uploaded successfully</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={clearIdDocument}
                      className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                    >
                      <X className="text-gray-400" size={20} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Notes */}
            <div className="mb-8">
              <Label htmlFor="additionalNotes" className="text-white mb-2 block">
                Additional Notes (Optional)
              </Label>
              <Textarea
                id="additionalNotes"
                name="additionalNotes"
                rows={4}
                value={formData.additionalNotes}
                onChange={handleChange}
                className="bg-slate-800 border-slate-600 text-white resize-none"
                placeholder="Any special instructions or questions about the installation..."
              />
            </div>

            {/* Terms Agreement */}
            <div className="mb-8 p-4 bg-slate-800/30 border border-slate-700 rounded-xl">
              <div className="flex items-start space-x-3">
                <input 
                  type="checkbox" 
                  required 
                  className="mt-1"
                  id="termsAgree"
                />
                <label htmlFor="termsAgree" className="text-sm text-gray-300">
                  I agree to HVGTEC's{' '}
                  <a href="/terms" target="_blank" className="text-cyan-400 hover:underline">
                    Terms and Conditions
                  </a>
                  , including the lock-in period requirements and equipment ownership policy.
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white py-6 text-lg rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Submitting Application...
                </>
              ) : (
                <>
                  Submit Application
                  <CheckCircle className="ml-2" size={20} />
                </>
              )}
            </Button>

            <p className="text-center text-sm text-gray-400 mt-4">
              📞 Need help? Call us at +63 123 456 7890 or chat via WhatsApp
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Apply;
